import React, { useMemo } from 'react';

import { Autocomplete, Checkbox, FormControlLabel, GridLegacy as Grid, MenuItem, TextField } from '@mui/material';

import { useGetInclusionsCategories } from '~/hooks/apis/inclusions/useGetInclusionsCategories';

import type { InclusionAssetEditValue } from './InclusionAssetEditDialog';

interface Props {
  isCustom?: boolean;
  open: boolean;
  value: InclusionAssetEditValue;
  onChange: (nextValue: InclusionAssetEditValue) => void;
}

export default function InclusionAssetEditFields({ isCustom = true, open, value, onChange }: Props) {
  const { data: inclusionCategories = [] } = useGetInclusionsCategories({ enabled: open });
  const categoryOptions = useMemo(
    () => [...inclusionCategories].sort((a, b) => a.category.localeCompare(b.category)),
    [inclusionCategories],
  );
  const selectedCategory =
    categoryOptions.find((category) => category.id === value.categoryId) ??
    categoryOptions.find((category) => category.name === value.category) ??
    null;

  return (
    <Grid container mt={1} spacing={2}>
      <Grid item sm={12}>
        <TextField
          value={value.name}
          label="Name"
          fullWidth
          onChange={(event) => onChange({ ...value, name: event.target.value })}
        />
      </Grid>
      <Grid item sm={5}>
        <TextField
          value={value.type}
          label="Type"
          fullWidth
          select
          disabled={!isCustom}
          onChange={(event) => onChange({ ...value, type: event.target.value })}
        >
          <MenuItem value="Flash">LTLE</MenuItem>
        </TextField>
      </Grid>
      <Grid item sm={7}>
        <Autocomplete
          groupBy={(option) => option.category}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, selectedValue) => option.name === selectedValue.name}
          onChange={(_event, category) =>
            onChange({
              ...value,
              category: category?.name ?? '',
              categoryId: category?.id ?? null,
            })
          }
          options={categoryOptions}
          renderInput={(params) => <TextField {...params} label="Category" />}
          value={selectedCategory}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          value={value.currency}
          label="Currency"
          fullWidth
          disabled={!isCustom}
          onChange={(event) => onChange({ ...value, currency: event.target.value })}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          value={value.rrp}
          label="RRP"
          fullWidth
          type="number"
          disabled={!isCustom}
          onChange={(event) => onChange({ ...value, rrp: Number(event.target.value) })}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          value={value.unit}
          label="Unit"
          fullWidth
          select
          onChange={(event) => onChange({ ...value, unit: event.target.value })}
        >
          <MenuItem value="Daily">Daily</MenuItem>
          <MenuItem value="Per Stay">Per Stay</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={value.isHidden}
              onChange={(event) => onChange({ ...value, isHidden: event.target.checked })}
            />
          }
          label="Hidden"
        />
      </Grid>
    </Grid>
  );
}
